import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { XCircleIcon } from '@heroicons/react/24/solid';
import InputField from '@medflex/components/stories/InputField';
import Button from '@medflex/components/stories/Button';

const clearKeywords = _.flow([_.uniq, _.filter(_.compose(_.negate(_.isEmpty), _.identity))]);

const INITIAL_ITEM_COUNT = 3;
const INITIAL_ITEM = '';
const INITIAL_ITEMS = [...Array(INITIAL_ITEM_COUNT)].map(() => INITIAL_ITEM);

const UserTagKeywords = ({ keywords, maxKeywords = 10, onChange }) => {
  const { t } = useTranslation('physician', { keyPrefix: 'modals.user_tags.creation.inputs' });
  const initialKeywords = keywords.length ? keywords : INITIAL_ITEMS;
  const [formKeywords, setFormKeywords] = useState(initialKeywords);

  const handleChange = useCallback(
    (value, index) => {
      const keywords = [...formKeywords];

      keywords[index] = value;

      setFormKeywords(keywords);
    },
    [formKeywords],
  );

  const handleAddKeyword = useCallback(() => {
    const keywords = [...formKeywords, INITIAL_ITEM];

    if (keywords.length > maxKeywords) return;

    setFormKeywords(keywords);
  }, [formKeywords, maxKeywords]);

  const handleRemoveKeyword = useCallback(
    index => {
      const keywords = [...formKeywords];

      if (keywords.length > 1) {
        keywords.splice(index, 1);
      } else {
        keywords[index] = INITIAL_ITEM;
      }

      setFormKeywords(keywords);
    },
    [formKeywords],
  );

  useEffect(() => {
    if (_.equals(keywords, clearKeywords(formKeywords))) return;
    onChange(clearKeywords(formKeywords));
  }, [keywords, formKeywords, onChange]);

  return (
    <div>
      <div className="text-sm text-slate-600 mb-2">{t('keywords.info')}</div>
      <div className="mb-4 space-y-4">
        {formKeywords.map((keyword, index) => {
          return (
            <div key={`keyword${index}`}>
              <div className="flex space-x-2">
                <InputField
                  name={`keyword${index}`}
                  value={keyword}
                  onChange={event => handleChange(event.target.value, index)}
                  maxLength={64}
                  className="w-full mb-1"
                />
                {formKeywords.length > 1 && (
                  <Button
                    onClick={() => handleRemoveKeyword(index)}
                    kind="link"
                    className="text-slate-600"
                  >
                    <XCircleIcon className="h-7 w-7 stroke-1" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {formKeywords.length !== maxKeywords && (
        <Button onClick={handleAddKeyword} kind="link" className="space-x-1 font-semibold">
          <PlusCircleIcon className="h-7 w-7 stroke-2" />
          <div>{t('keywords.add_keyword')}</div>
        </Button>
      )}
    </div>
  );
};

UserTagKeywords.propTypes = {
  keywords: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxKeywords: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

export default UserTagKeywords;
